import React, { useState, useMemo, useEffect, useRef } from 'react';

import { SelectMultiple as SelectMultipleTea, ConfigProvider, Bubble } from 'tea-component';
import SelectButton from '../SelectButton';



import './index.scss';
import { Props } from './types';

/**
 * 多选下拉默认搜索：匹配 value（不区分大小写）、value_cn、value_en、tooltip/text（字符串）子串
 */
export const defaultSelectMultipleFilter = (
  inputValue: string,
  option: {
    value?: string;
    value_cn?: string;
    value_en?: string;
    text?: unknown;
    tooltip?: unknown;
  },
): boolean => {
  const query = String(inputValue ?? '').trim().toLowerCase();
  if (!query) {
    return true;
  }
  const tooltipStr =
    typeof option.tooltip === 'string' ? option.tooltip : '';
  const textStr = typeof option.text === 'string' ? option.text : '';
  const haystacks = [
    option.value,
    option.value_cn,
    option.value_en,
    tooltipStr || textStr,
  ]
    .filter((v) => v !== undefined && v !== null && String(v).length > 0)
    .map((v) => String(v).toLowerCase());
  return haystacks.some((h) => h.includes(query));
};

const SelectMultiple = (props: Props) => {
  const {
    value,
    defaultValue,
    minSelect = 0,
    isEn,
    isFirst,
    options = [],
    label,
    lastOneWarn = true,
    lastOneMessage = 'This option is the last one, after unchecking the competitor game will not have data.',
    onChange,
    onClose,
    filter: filterProp,
    ...othersProps
  } = props;
  const defaultValueInner = options?.map(item => item.value) as string[];
  const selectHistory = useRef([] as any);
  const [selectValue, setSelectValue] = useState(value ?? defaultValue ?? defaultValueInner);
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  let valueList = options?.filter(item => selectValue?.includes(item.value))?.map(item => item.text);
  if (options?.length > minSelect && options?.length > 0 && options?.length === selectValue?.length || isFirst) {
    valueList = ['All'];
  }

  const selectOptions = useMemo(() => options.map?.((item) => ({
    ...item,
    value: item?.value,
    text: (
      <p className='select-multiple-item'>
        <img className='select-multiple-item-icon' src={item.icon} />
        <span className='select-multiple-item-text'>{item.text}</span>
      </p>
    ),
    tooltip: item.text
  })), [options]);

  const handleChange = (value: string[]) => {
    onChange?.(value);
    setSelectValue(value);
  };

  const handleOpenChange = (visible: boolean) => {
    selectHistory.current = [];
    setDropdownVisible(visible);
    if (!visible) {
      onClose?.(selectValue);
    }
  };

  useEffect(() => {
    if (!value || !options) return;
    setSelectValue(value);
  }, [value, JSON.stringify(options)]);

  // 记录选择历史
  useEffect(() => {
    selectHistory.current = [
      ...selectHistory.current,
      selectValue
    ];
  }, [JSON.stringify(selectValue)]);

  // 选择到最后一条时展示提示框，关闭弹窗重置显示逻辑
  useEffect(() => {
    setBubbleVisible(
      selectHistory?.current?.[selectHistory.current?.length - 2]?.length > 0 &&
      selectValue?.length === 1 && lastOneWarn &&
      dropdownVisible
    );
  }, [selectValue, lastOneWarn, dropdownVisible]);

  return (
    <ConfigProvider locale={isEn ? "en" : "zh"}>
      <Bubble
        dark
        visible={bubbleVisible}
        content={lastOneMessage}
      >
        <SelectMultipleTea
          {...othersProps}
          appearance='button'
          boxClassName='select-multiple-container dbl-select-multiple-container'
          overlayClassName={`${othersProps?.overlayClassName || ''} dbl-select-multiple-overlay search-item`}
          matchButtonWidth={true}
          staging={false}
          value={selectValue}
          defaultValue={selectValue}
          button={<SelectButton label={label} selectList={valueList as any} />}
          onChange={handleChange}
          options={selectOptions}
          allOption={{
            value: 'All',
            text: 'All'
          }}
          onOpenChange={handleOpenChange}
          searchable
          filter={filterProp ?? defaultSelectMultipleFilter}
        />
      </Bubble>
    </ConfigProvider>
  );
};

export default SelectMultiple;
